import React, { useState, useEffect, Fragment } from 'react';
import { useQueryClient, useQuery, useMutation } from 'react-query';
import { Route, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { IntlProvider } from 'react-intl';

import { Loader } from 'components';
import Header from './containers/layout/header';
import Bag from './containers/bag/bag';

import Home from './pages/home';
import Choose from './pages/choose';
import Amount from './pages/amount';
import AmountEdit from './pages/amount-edit';
import Experience from './pages/experience';
import ExperienceEdit from './pages/experience-edit';
import Sender from './pages/sender';
import Review from './pages/review';
import Payment from './pages/payment';
import ThankYou from './pages/thankyou';
import Terms from './pages/terms';
import getDefaultLanguage from './utils/utils';

import { translationMessages as messages } from './language';

function App() {
    const [initialLoading, setInitialLoading] = useState(true);

    const { slug } = useParams();
    const queryClient = useQueryClient();

    const restaurant = useQuery('restaurant')?.data || {};
    const language = useQuery('language')?.data || 'en';
    const domain = queryClient.getQueryData('restaurant')?.domain || '';
    const toggle = useQuery('featureToggle')?.data;

    const restaurantMutation = useMutation('restaurant-mutation');
    const paymentMutation = useMutation('payment-mutation');
    const experiencesMutation = useMutation('experiences-mutation');
    const languageMutation = useMutation('language-mutation');
    const featureToggleMutation = useMutation('feature-toggle-mutation');

    const adyenEnv = process.env.REACT_APP_ADYEN_ENV;
    const adyenHash = process.env.REAC_APP_ADYEN_HASH;

    useEffect(() => {
        languageMutation.mutate(getDefaultLanguage());
        restaurantMutation.mutate(slug);
        localStorage.setItem('restaurant', slug);
    }, []);

    useEffect(() => {
        if (restaurant.id) {
            experiencesMutation.mutate(restaurant.id);
            featureToggleMutation.mutate(restaurant.id);
            paymentMutation.mutate(slug);

            const bag = localStorage.getItem('bag') ? JSON.parse(localStorage.getItem('bag')) : [];
            const sender = localStorage.getItem('sender')
                ? JSON.parse(localStorage.getItem('sender'))
                : [];

            queryClient.setQueryData('bag', bag);
            queryClient.setQueryData('sender', sender);

            setInitialLoading(false);
        }
    }, [restaurant.id, toggle]);

    const checkCanSee = () => {
        if (toggle && toggle.GiftCard === false) {
            window.location = `${process.env.REACT_APP_GIFT_CARD_ENV}/${domain}`;
        }
    };

    return (
        <IntlProvider locale={language} messages={messages[language]}>
            {initialLoading ||
            restaurantMutation.isLoading ||
            experiencesMutation.isLoading ||
            featureToggleMutation.isLoading ? (
                <Fragment>
                    {checkCanSee()}
                    <Loader />
                </Fragment>
            ) : (
                <Fragment>
                    <div className="App">
                        <Header />
                        <Bag />
                        <Route exact path={`/${domain}`} component={Home} />
                        <Route exact path={`/${domain}/choose`} component={Choose} />
                        <Route exact path={`/${domain}/amount`} component={Amount} />
                        <Route exact path={`/${domain}/amount/edit/:id`} component={AmountEdit} />
                        <Route exact path={`/${domain}/experience`} component={Experience} />
                        <Route
                            exact
                            path={`/${domain}/experience/edit/:id`}
                            component={ExperienceEdit}
                        />
                        <Route exact path={`/${domain}/sender`} component={Sender} />
                        <Route exact path={`/${domain}/review`} component={Review} />
                        <Route exact path={`/${domain}/payment`} component={Payment} />
                        <Route exact path={`/${domain}/:order/payment`} component={Payment} />
                        <Route exact path={`/${domain}/thankyou`} component={ThankYou} />
                        <Route exact path={`/${domain}/terms`} component={Terms} />
                    </div>

                    {domain && (
                        <Helmet>
                            <script
                                type="text/javascript"
                                src={`https://checkoutshopper-${adyenEnv}.adyen.com/checkoutshopper/assets/js/sdk/checkoutSecuredFields.1.3.0.min.js`}
                            ></script>
                            <script
                                src={`https://checkoutshopper-${adyenEnv}.adyen.com/checkoutshopper/sdk/3.8.0/adyen.js`}
                                integrity={adyenHash}
                                crossorigin="anonymous"
                            ></script>
                            <link
                                rel="stylesheet"
                                href={`https://checkoutshopper-${adyenEnv}.adyen.com/checkoutshopper/sdk/3.8.0/adyen.css`}
                                integrity="sha384-y1lKqffK5z+ENzddmGIfP3bcMRobxkjDt/9lyPAvV9H3JXbJYxCSD6L8TdyRMCGM"
                                crossorigin="anonymous"
                            />
                        </Helmet>
                    )}
                </Fragment>
            )}
        </IntlProvider>
    );
}

export default React.memo(App);
